import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { WorkbookDetail } from '@erp/rpc';
import { WORKBOOK_FRESH_MS } from './workbookQueryTimes';
import { orpc } from '@/api/rpc/client';
import { invalidateNamespaceQueries } from '@/api/rpc/hooks/namespaces/useNamespaces';
import { NamespaceField } from '@/components/namespaces/NamespaceField';
import {
  namespaceIdFromSelectValue,
  selectValueFromNamespaceId,
  unassignedNamespaceOption,
} from '@/components/namespaces/namespaceLabels';
import { toast } from '@/components/ui/toast';

/**
 * The open Workbook's namespace. It is Workbook metadata, not part of the
 * Definition, so it saves on its own and never touches the Revision.
 */
export function WorkbookNamespaceField({
  workbookId,
  disabled,
  label,
}: {
  workbookId: string;
  disabled: boolean;
  label: ReactNode;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const getKey = orpc.workbooks.get.queryOptions({
    input: { workbookId },
  }).queryKey;
  // The page's own read of the Workbook, kept current by the editor's saves:
  // mounting here must not fetch it again.
  const workbook = useQuery(
    orpc.workbooks.get.queryOptions({
      input: { workbookId },
      staleTime: WORKBOOK_FRESH_MS,
    }),
  );
  const setNamespace = useMutation(
    orpc.workbooks.setNamespace.mutationOptions({
      onSuccess: (summary) => {
        queryClient.setQueryData<WorkbookDetail>(getKey, (current) =>
          current ? { ...current, namespaceId: summary.namespaceId } : current,
        );
        void queryClient.invalidateQueries({
          queryKey: orpc.workbooks.list.key(),
        });
        invalidateNamespaceQueries(queryClient);
      },
      onError: (error) =>
        toast.error(
          gt('Could not update namespace', {
            context: 'toast after workbook namespace assignment fails',
          }),
          { description: error.message },
        ),
    }),
  );
  return (
    <NamespaceField
      value={selectValueFromNamespaceId(workbook.data?.namespaceId)}
      onChange={(next) =>
        setNamespace.mutate({
          workbookId,
          namespaceId: namespaceIdFromSelectValue(next),
        })
      }
      noneOption={unassignedNamespaceOption(gt)}
      disabled={disabled || !workbook.data || setNamespace.isPending}
      label={label}
    />
  );
}
